/**
 * AG Grid Column Builder for Benefit Plan Groups (API-driven)
 */

import { logger } from "@platform/browser-logger";
import {
  BaseBenefitPlanPolicyPeriod,
  BenefitPlanTypeEnum,
  CoverageTypeEnum,
  NetworkEnum,
  type PlanOptionNameEnum,
} from "@platform/openapi-types";
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ColSpanParams,
  Column,
  GetMainMenuItems,
  ProvidedColumnGroup,
} from "ag-grid-community";

import { BenefitPlanGroup, RfpCoverage } from "../../../api/types";
import { BenefitDefinition } from "../../../benefits/benefit.types";
import EditablePlanOptionHeader from "../components/HeaderComponents/EditablePlanOptionHeader";
import PlanLevelHeader from "../components/HeaderComponents/PlanLevelHeader";
import PlanOptionGroupHeader from "../components/HeaderComponents/PlanOptionGroupHeader";
import { isValuePresent } from "../../../locs/requiredFields/validation";

import { formatEnumValue, formatPolicyPeriodDisplay } from "./enumFormatters";
import {
  CLASSNAMES,
  createBenefitsColumn,
  getCellClasses,
  isChildRow,
} from "./buildColumns";
import {
  ColumnBuildStrategy,
  LeafColumnBehaviorBuilder,
} from "./columnBuildStrategy";

/** Maps plan UUID (btxIdentifier) → source document filename(s). */
export type PlanDocumentMap = Record<string, string[]>;

/**
 * Returns the display header for a plan.
 * - If option name is meaningful (not "Not Applicable"), shows "OptionName - Identifier"
 * - If option name is "Not Applicable" or missing, shows just the identifier
 */
const NOT_APPLICABLE = "notApplicable";

const formatOptionHeader = (plan: {
  optionName?: PlanOptionNameEnum;
  identifier: string;
}): string => {
  const formattedOption = formatEnumValue(plan.optionName);
  if (formattedOption && plan.optionName !== NOT_APPLICABLE) {
    return `${formattedOption} - ${plan.identifier}`;
  }

  return plan.identifier;
};

/**
 * Wraps a plan column (ColDef or ColGroupDef) in a document-level ColGroupDef
 * when planDocumentMap is provided. If the map is undefined (feature flag off)
 * the column is returned as-is.
 */
const wrapWithDocumentHeader = (
  column: ColDef | ColGroupDef,
  planId: string,
  planDocumentMap: PlanDocumentMap | undefined,
  zebraClassNames: string[],
): ColDef | ColGroupDef => {
  if (!planDocumentMap) return column;
  const docNames = planDocumentMap[planId] ?? [];
  const docName = docNames.join(", ");
  return {
    headerName: docName,
    headerTooltip: docName,
    marryChildren: true,
    headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_1, ...zebraClassNames],
    children: [column],
  } as ColGroupDef;
};

/**
 * Creates a benefits column with labelled header tiers matching the document
 * header structure. When planDocumentMap is provided, the flat benefits ColDef
 * is wrapped in ColGroupDefs with "Source Document" and (when networks exist)
 * "Option Name" labels so the left-most column describes each header row.
 *
 * Without planDocumentMap the plain flat benefits column is returned.
 */
const BENEFITS_HEADER_CLASS = "benefits-header";
const PLAN_GROUP_ID_TOOLTIP =
  "This is the unique ID for this plan within the plan group";
// BTX-only: matches the header naming the Plan Level dropdown uses on the
// plan columns themselves. Never the default — MPX's carrier-grouped grid
// shares this builder and has no such tier.
const PLAN_LEVEL_TIER_LABEL = "Plan Level";
// Header label for the Current Carrier - Policy group/benefits column (ADR 10.22).
const CURRENT_POLICY_HEADER_NAME = "In-Force Policy Name";
const CURRENT_POLICY_HEADER_TOOLTIP =
  "This policy data is sourced from Salesforce";

/**
 * The benefits column is `lockVisible`, and the tiers wrapping it carry no
 * columns of their own — they exist so the left column's header rows line up
 * with the plan columns beside it. The columns tool panel lists every tier as
 * its own row, which surfaces that scaffolding as a branch of permanently
 * disabled entries alongside the real plans. The panel walks groups top down
 * and stops at the first suppressed node, so suppressing the outermost one
 * drops the whole branch.
 */
const hideFromColumnsToolPanel = <TColumn extends ColDef | ColGroupDef>(
  column: TColumn,
): TColumn => ({ ...column, suppressColumnsToolPanel: true });

const buildLabelledBenefitsColumn = (
  planDocumentMap: PlanDocumentMap | undefined,
  hasNetworks: boolean,
  // When set (current coverage + feature on), labels the top header tier of the
  // benefits column to align with the group-header policy dropdown (ADR 10.22).
  currentPolicyLabel?: string,
  // Label for the option-name tier. Empty by default (MPX's carrier-grouped
  // grid, which shares this builder, has no such label); BTX's caller passes
  // "Plan Level" to match the header naming its own plan columns use.
  optionTierLabel = "",
): ColDef | ColGroupDef => {
  const benefitsCol = createBenefitsColumn();
  const topHeaderName = currentPolicyLabel ?? "";

  // When the current-policy feature is on, the plan group gains an extra header
  // row (original group label + dropdown row). Wrap the benefits column in an
  // empty outer group so the left column depth stays aligned.
  const wrapForCurrentPolicy = (col: ColDef | ColGroupDef): ColGroupDef =>
    ({
      headerName: "",
      headerClass: BENEFITS_HEADER_CLASS,
      marryChildren: true,
      children: [col],
    }) as ColGroupDef;

  if (!planDocumentMap) {
    // Without document tiers there's no wrapping group; add one only when we
    // need the "In-Force Policy Name" label so the header row stays aligned.
    if (!currentPolicyLabel) return benefitsCol;

    const dropdownRow: ColGroupDef = {
      headerName: currentPolicyLabel,
      headerTooltip: CURRENT_POLICY_HEADER_TOOLTIP,
      headerClass: BENEFITS_HEADER_CLASS,
      marryChildren: true,
      children: [benefitsCol],
    };
    return wrapForCurrentPolicy(dropdownRow);
  }

  // Clear the header text — the wrapping tiers provide the labels
  if (hasNetworks) {
    // (optional extra tier) → Source Document → "" → Plan Group ID → (empty leaf)
    benefitsCol.headerName = "";
    const col: ColGroupDef = {
      headerName: topHeaderName,
      headerTooltip: currentPolicyLabel
        ? CURRENT_POLICY_HEADER_TOOLTIP
        : undefined,
      headerClass: BENEFITS_HEADER_CLASS,
      marryChildren: true,
      children: [
        {
          headerName: "Source Document",
          headerClass: BENEFITS_HEADER_CLASS,
          marryChildren: true,
          children: [
            {
              headerName: optionTierLabel,
              headerClass: BENEFITS_HEADER_CLASS,
              marryChildren: true,
              children: [
                {
                  headerName: "Plan Group ID",
                  headerTooltip: PLAN_GROUP_ID_TOOLTIP,
                  headerClass: BENEFITS_HEADER_CLASS,
                  marryChildren: true,
                  children: [benefitsCol],
                },
              ],
            },
          ],
        },
      ],
    };
    return currentPolicyLabel ? wrapForCurrentPolicy(col) : col;
  }

  // (optional extra tier) → Source Document → "" → Plan Group ID (leaf)
  benefitsCol.headerName = "Plan Group ID";
  benefitsCol.headerTooltip = PLAN_GROUP_ID_TOOLTIP;
  const col: ColGroupDef = {
    headerName: topHeaderName,
    headerTooltip: currentPolicyLabel
      ? CURRENT_POLICY_HEADER_TOOLTIP
      : undefined,
    headerClass: BENEFITS_HEADER_CLASS,
    marryChildren: true,
    children: [
      {
        headerName: "Source Document",
        headerClass: BENEFITS_HEADER_CLASS,
        marryChildren: true,
        children: [
          {
            headerName: optionTierLabel,
            headerClass: BENEFITS_HEADER_CLASS,
            marryChildren: true,
            children: [benefitsCol],
          },
        ],
      },
    ],
  };
  return currentPolicyLabel ? wrapForCurrentPolicy(col) : col;
};

export const createLabelledBenefitsColumn = (
  ...args: Parameters<typeof buildLabelledBenefitsColumn>
): ColDef | ColGroupDef =>
  hideFromColumnsToolPanel(buildLabelledBenefitsColumn(...args));

const buildBenefitsColumnWithHeaderLabels = (
  headerLabels: string[],
): ColDef | ColGroupDef => {
  const labels = headerLabels.filter((label) => label.trim().length > 0);
  const benefitsCol = createBenefitsColumn();

  if (labels.length === 0) return benefitsCol;

  benefitsCol.headerName = labels[labels.length - 1];

  return labels.slice(0, -1).reduceRight<ColDef | ColGroupDef>(
    (child, label) => ({
      headerName: label,
      marryChildren: true,
      headerClass: BENEFITS_HEADER_CLASS,
      children: [child],
    }),
    benefitsCol,
  );
};

export const createBenefitsColumnWithHeaderLabels = (
  headerLabels: string[],
): ColDef | ColGroupDef =>
  hideFromColumnsToolPanel(buildBenefitsColumnWithHeaderLabels(headerLabels));

const buildHierarchy = (...labels: (string | undefined)[]): string[] =>
  labels.filter(
    (label): label is string =>
      typeof label === "string" && label.trim().length > 0,
  );

/**
 * Label for a plan's own header row. Superseded quote-history columns are marked
 * here because the Quotes grid omits the plan-group row that otherwise carries
 * the suffix, and each column states its own version independently.
 */
const planTierHeaderName = (
  plan: { identifier: string },
  isHistorical: boolean | undefined,
): string => (isHistorical ? `${plan.identifier} (Previous)` : plan.identifier);

// Consistent column width for aligned grid sync with RatesGrid/SummaryGrid
const MIN_COLUMN_WIDTH = 450;
// Half width for network sub-columns (in-network/out-of-network) so total width matches non-network columns
const MIN_NETWORK_COLUMN_WIDTH = MIN_COLUMN_WIDTH / 2;

/**
 * Calculates zebra stripe class names based on iteration index
 *
 * @param index - Group iteration index (0-based)
 * @param policyPeriod - Policy period for type-specific styling
 * @returns Array of zebra stripe class names
 */
const calculateZebraClassNames = (
  index: number,
  policyPeriod?: BaseBenefitPlanPolicyPeriod,
): string[] => {
  const zebraColumnEvenOdd = index % 2 === 0 ? CLASSNAMES.EVEN : CLASSNAMES.ODD;

  const zebraColumnClassType = policyPeriod
    ?.toLowerCase()
    ?.replace(/\s+/g, "")
    ?.trim();

  return [
    CLASSNAMES.ZEBRA_COLUMN,
    `${CLASSNAMES.ZEBRA_COLUMN_PREFIX}${zebraColumnEvenOdd}`,
    `${CLASSNAMES.ZEBRA_COLUMN_TYPE_PREFIX}${zebraColumnClassType}`,
  ];
};

/**
 * The ColumnContext is arbitrary data AG Grid allows on Column definitions.
 * In our case, we add plan group info like the product type in order to
 * facilitate context-aware menu actions later on.
 */
export interface ColumnContext {
  productType: BenefitPlanTypeEnum;
  planGroupId: string;
  benefitPlanId?: string;
  optionName?: PlanOptionNameEnum;
  identifier?: string;
  size: number;
  position: number;
  policyPeriod?: BaseBenefitPlanPolicyPeriod;
  nickname?: string;
  groupHeader?: string;
}

const isColumnContext = (context: unknown): context is ColumnContext =>
  typeof context === "object" &&
  context !== null &&
  "productType" in context &&
  "planGroupId" in context &&
  "size" in context &&
  "position" in context;

const createColumnContext = (
  group: BenefitPlanGroup,
  plan?: { id: string; optionName?: PlanOptionNameEnum; identifier: string },
  groupHeader?: string,
): ColumnContext => ({
  productType: group.productType,
  // The flattened Quotes adapter keys `id` by the underlying plan, not the
  // plan group — fall back to it only when there's no real backing group.
  planGroupId: group.proposalPlanGroupId ?? group.id,
  benefitPlanId: plan?.id,
  optionName: plan?.optionName,
  identifier: plan?.identifier,
  size: group.size,
  position: group.position,
  policyPeriod: group.policyPeriod,
  nickname: group.nickname,
  groupHeader,
});

export const extractColumnContext = (column: Column): ColumnContext | null => {
  const context: unknown = column.getColDef().context;
  return isColumnContext(context) ? (context as ColumnContext) : null;
};

export const extractColumnGroupContext = (
  columnGroup: ProvidedColumnGroup,
): ColumnContext | null => {
  const context: unknown = columnGroup.getColGroupDef()?.context;
  return isColumnContext(context) ? (context as ColumnContext) : null;
};

/**
 * Given benefit plan groups and coverage, determines available networks.
 * Business rules:
 *  - Always build network columns for Dental PPO plans.
 *  - For other plan types, network columns are built if the product type is PPO or the coverage type is dental.
 *  - If benefitPlanGroups is empty, returns an empty array (no networks available).
 * @param benefitPlanGroups - Array of benefit plan groups (assumed to have the same product type)
 * @param coverage - The RFP coverage object
 * @returns Available network options array with NetworkEnum values, or empty array if not applicable or input is empty
 */
const getNetworks = (
  benefitPlanGroups: BenefitPlanGroup[],
  coverage: RfpCoverage,
): readonly string[] => {
  if (!benefitPlanGroups || benefitPlanGroups.length === 0) {
    // Explicitly handle empty array case: no networks available
    return [];
  }
  const firstProductType = benefitPlanGroups[0]?.productType;
  const isPPO = firstProductType === BenefitPlanTypeEnum.ppo;
  const isDentalCoverage = coverage.coverageType === CoverageTypeEnum.dental;

  // Only Dental PPO plans have network options
  if (isPPO && isDentalCoverage) {
    return [NetworkEnum.inNetwork, NetworkEnum.outOfNetwork];
  }
  return [];
};

/**
 * Creates column definition for plan options and networks strategy (3-tier)
 *
 * @param group - Benefit plan group
 * @param networks - Available network options
 * @param zebraClassNames - Zebra stripe class names
 * @param leafBehaviorBuilder - Builder for leaf column behavior based on strategy
 * @param alternateIndex - Optional index for alternate plans to differentiate header display
 * @returns Column group definition
 */
type PlanOptionsAndNetworksPlan = BenefitPlanGroup["benefitPlans"][number];

const groupNetworkFieldsByPlanId = (
  displayedColumns: readonly Column[],
): ReadonlyMap<string, readonly string[]> => {
  const planFields = displayedColumns
    .map((column) => column.getColDef().field)
    .filter(
      (field): field is string =>
        typeof field === "string" && field.includes("."),
    );
  const planIds = [...new Set(planFields.map((field) => field.split(".")[0]))];
  return new Map(
    planIds.map((planId) => [
      planId,
      planFields.filter((field) => field.startsWith(`${planId}.`)),
    ]),
  );
};

// colSpan runs for every cell of every rendered row, so scanning all displayed
// columns per call is cells × columns work. AG Grid returns the same array from
// getAllDisplayedColumns until the displayed columns change, which makes it a
// safe cache key.
const networkFieldsCache = new WeakMap<
  readonly Column[],
  ReadonlyMap<string, readonly string[]>
>();

const getVisiblePlanNetworkFields = (
  params: ColSpanParams<{ benefit: BenefitDefinition }>,
  planId: string,
): readonly string[] => {
  const displayedColumns = params.api.getAllDisplayedColumns();
  const cached = networkFieldsCache.get(displayedColumns);
  const fieldsByPlanId = cached ?? groupNetworkFieldsByPlanId(displayedColumns);
  if (!cached) networkFieldsCache.set(displayedColumns, fieldsByPlanId);
  return fieldsByPlanId.get(planId) ?? [];
};

/** Builds the in-network/out-of-network leaf columns for one plan. */
const buildNetworkChildren = (
  plan: PlanOptionsAndNetworksPlan,
  networks: readonly string[],
  zebraClassNames: string[],
  leafBehaviorBuilder: LeafColumnBehaviorBuilder,
  useFieldAsColId: boolean,
  groupHeader: string,
  planHeader: string,
): ColDef[] =>
  networks.map((networkName: string) => {
    const networkHeader = formatEnumValue(networkName) || networkName;

    return {
      colId: useFieldAsColId
        ? `${plan.id}.${networkName}`
        : `${plan.identifier}.${networkName}`,
      field: `${plan.id}.${networkName}`,
      headerName: networkHeader,
      suppressHeaderContextMenu: true,
      headerClass: [
        CLASSNAMES.HEADER_COLUMN_LEVEL_2,
        CLASSNAMES.BENEFIT_CELL,
        ...zebraClassNames,
      ],
      cellClass: (params: CellClassParams) =>
        getCellClasses(params, `${plan.id}.${networkName}`, zebraClassNames),
      headerTooltip: `${plan.identifier}.${networkName}`,
      cellRendererParams: {
        hierarchy: buildHierarchy(groupHeader, planHeader, networkHeader),
        isNetworkBenefit: true,
      },
      // Leaf behavior (suppressMovable, cellClassRules, etc.) spreads first
      // so that the network-specific minWidth below wins over the full-column
      // minWidth in the leaf behavior, keeping sub-column widths at half the
      // standard plan-column width for correct alignedGrids sync.
      ...leafBehaviorBuilder.build(),
      flex: 1,
      minWidth: MIN_NETWORK_COLUMN_WIDTH,
      colSpan: (params: ColSpanParams<{ benefit: BenefitDefinition }>) => {
        const isNetworked = params.data?.benefit?.networkDependent;
        if (isNetworked) {
          return 1;
        }

        // For non-networked benefits, span across only the currently
        // visible network columns for this plan.
        const visiblePlanNetworkFields = getVisiblePlanNetworkFields(
          params,
          plan.id,
        );
        if (visiblePlanNetworkFields.length === 0) {
          return 0;
        }

        const currentField = `${plan.id}.${networkName}`;
        const isFirstVisibleField =
          visiblePlanNetworkFields[0] === currentField;

        return isFirstVisibleField ? visiblePlanNetworkFields.length : 0;
      },
    };
  });

/**
 * Builds the tier(s) below the Plan Level row: the Plan Group ID row
 * (doc-map path), the Quotes "Plan Option" row (carrier-grouped, no doc map),
 * or the plain network leaves.
 */
const buildOptionGroupChildren = (
  group: BenefitPlanGroup,
  plan: PlanOptionsAndNetworksPlan,
  groupHeader: string,
  zebraClassNames: string[],
  networkChildren: ColDef[],
  planDocumentMap: PlanDocumentMap | undefined,
  showSeparatePlanOptionRow: boolean,
  showPlanLevelDropdown: boolean,
  planLevelMenuItems: GetMainMenuItems | undefined,
): (ColDef | ColGroupDef)[] => {
  if (planDocumentMap) {
    return [
      {
        // Plan Group ID row — carries the 3-dot plan menu (moved down
        // from the Plan Level row above, which hosts the level dropdown
        // instead) only when the dropdown is enabled; otherwise this
        // tier stays a plain label, as it always has for MPX.
        groupId: `${group.id}-planid-${plan.id}`,
        headerName: plan.identifier,
        headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_2, ...zebraClassNames],
        suppressHeaderContextMenu: showPlanLevelDropdown
          ? !planLevelMenuItems
          : true,
        marryChildren: true,
        ...(showPlanLevelDropdown && {
          context: createColumnContext(group, plan, groupHeader),
          mainMenuItems: planLevelMenuItems,
          ...(planLevelMenuItems && {
            headerGroupComponent: PlanOptionGroupHeader,
          }),
        }),
        children: networkChildren,
      },
    ];
  }

  if (!showSeparatePlanOptionRow) {
    return networkChildren;
  }

  const optionLabel =
    plan.optionName !== NOT_APPLICABLE ? formatEnumValue(plan.optionName) : "";
  return [
    {
      // Dedicated "Plan Option" tier (Low/High) between the Plan row
      // and the network headers. Distinct groupId per plan so AG Grid
      // keeps sibling options as separate groups. The header cell is
      // click-to-edit (dropdown + custom text) via the app-supplied
      // PlanOptionEditor context (CD-1261).
      groupId: `${group.id}-optionname-${plan.id}`,
      headerName: optionLabel,
      headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_1, ...zebraClassNames],
      suppressHeaderContextMenu: true,
      marryChildren: true,
      headerGroupComponent: EditablePlanOptionHeader,
      headerGroupComponentParams: {
        planId: plan.id,
        optionLabel,
      },
      children: networkChildren,
    },
  ];
};

/** Builds the Plan Level row: the 3-dot menu, or the BTX dropdown when enabled. */
const buildPlanLevelOptionGroup = (
  group: BenefitPlanGroup,
  plan: PlanOptionsAndNetworksPlan,
  groupHeader: string,
  zebraClassNames: string[],
  optionGroupHeader: string,
  optionGroupChildren: (ColDef | ColGroupDef)[],
  showPlanLevelDropdown: boolean,
  planLevelMenuItems: GetMainMenuItems | undefined,
  rfpId: string | undefined,
): ColGroupDef => ({
  // Distinct per plan so AG Grid doesn't collapse sibling options that
  // share a headerName (e.g. both default to optionName "notApplicable")
  // into one ProvidedColumnGroup, which would resolve the menu's context
  // to whichever option registered first.
  groupId: `${group.id}-option-${plan.id}`,
  headerName: optionGroupHeader,
  // Plan Level row: the level dropdown replaces the 3-dot menu (moved
  // to the Plan Group ID row above) only when enabled for BTX.
  suppressHeaderContextMenu: showPlanLevelDropdown ? true : !planLevelMenuItems,
  marryChildren: true,
  context: createColumnContext(group, plan, groupHeader),
  headerClass: [
    CLASSNAMES.HEADER_COLUMN_LEVEL_1,
    ...zebraClassNames,
    ...(showPlanLevelDropdown && !isValuePresent(plan.optionName)
      ? [CLASSNAMES.HEADER_REQUIRED_MISSING]
      : []),
  ],
  mainMenuItems: showPlanLevelDropdown ? undefined : planLevelMenuItems,
  ...(showPlanLevelDropdown
    ? {
        headerGroupComponent: PlanLevelHeader,
        headerGroupComponentParams: {
          rfpId,
          benefitPlanId: plan.id,
          optionName: plan.optionName,
        },
      }
    : planLevelMenuItems && {
        headerGroupComponent: PlanOptionGroupHeader,
      }),
  children: optionGroupChildren,
});

const createPlanOptionsAndNetworksColumnsFromGroup = (
  group: BenefitPlanGroup,
  networks: readonly string[],
  zebraClassNames: string[],
  leafBehaviorBuilder: LeafColumnBehaviorBuilder,
  planLevelMenuItems?: GetMainMenuItems,
  alternateIndex?: number,
  planDocumentMap?: PlanDocumentMap,
  // When true, the colId is keyed off plan.id (matching `field`) rather than the
  // shared plan.identifier. The carrier-grouped (MPX) grid requires colId === field:
  // the popup editors save via node.setDataValue(colDef.field), and AG Grid resolves
  // that key by colId, so a colId ≠ field silently drops the write.
  useFieldAsColId = false,
  // Needed by the BTX Plan Level dropdown (PlanLevelHeader) to persist option
  // name changes; only rendered when enablePlanLevelDropdown is set below.
  rfpId?: string,
  // Explicit seam: the BTX Plan Level dropdown is a Build-only concern, so it
  // must not key off planDocumentMap alone — MPX's carrier-grouped columns
  // (buildCarrierGroupedColumns) also pass a planDocumentMap, but for its own
  // "Source Document" header labels, not for this. Only buildColumnsFromBenefitPlanGroups
  // (the BTX-only entry point) sets this true.
  enablePlanLevelDropdown = false,
): ColGroupDef => {
  const groupHeader = formatPolicyPeriodDisplay(
    group.policyPeriod,
    group.nickname,
    alternateIndex,
    group.isHistorical,
  );

  return {
    headerName: groupHeader,
    marryChildren: true,
    headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_0, ...zebraClassNames],
    context: createColumnContext(group),
    children: group.benefitPlans.map((plan) => {
      const planHeader = formatOptionHeader(plan);
      // Quotes (carrier-grouped) SBS grid splits the option out of the "Plan"
      // row into its own tier above the network headers: the Plan row shows the
      // bare plan name and a dedicated "Plan Option" row shows Low/High.
      const showSeparatePlanOptionRow = useFieldAsColId && !planDocumentMap;
      const optionGroupHeader = planDocumentMap
        ? formatEnumValue(plan.optionName) || plan.identifier
        : showSeparatePlanOptionRow
          ? planTierHeaderName(plan, group.isHistorical)
          : planHeader;

      const networkChildren = buildNetworkChildren(
        plan,
        networks,
        zebraClassNames,
        leafBehaviorBuilder,
        useFieldAsColId,
        groupHeader,
        planHeader,
      );

      // The BTX Plan Level dropdown is Build-only: gate it on the explicit
      // enablePlanLevelDropdown seam, not on planDocumentMap alone — MPX's
      // carrier-grouped columns (buildCarrierGroupedColumns) also pass a
      // planDocumentMap, but for its own "Source Document" header labels.
      const showPlanLevelDropdown =
        !!planDocumentMap && enablePlanLevelDropdown;

      const optionGroupChildren = buildOptionGroupChildren(
        group,
        plan,
        groupHeader,
        zebraClassNames,
        networkChildren,
        planDocumentMap,
        showSeparatePlanOptionRow,
        showPlanLevelDropdown,
        planLevelMenuItems,
      );

      const optionGroup = buildPlanLevelOptionGroup(
        group,
        plan,
        groupHeader,
        zebraClassNames,
        optionGroupHeader,
        optionGroupChildren,
        showPlanLevelDropdown,
        planLevelMenuItems,
        rfpId,
      );

      return wrapWithDocumentHeader(
        optionGroup,
        plan.id,
        planDocumentMap,
        zebraClassNames,
      );
    }),
  };
};

const wrapWithEditablePlanOptionTier = (
  group: BenefitPlanGroup,
  plan: { id: string; optionName?: PlanOptionNameEnum; identifier: string },
  groupHeader: string,
  leafCol: ColDef,
  planLevelMenuItems: GetMainMenuItems | undefined,
  zebraClassNames: string[],
  tierHeaderName: string = plan.identifier,
): ColGroupDef => {
  const optionLabel =
    plan.optionName !== NOT_APPLICABLE ? formatEnumValue(plan.optionName) : "";

  const optionLeaf: ColDef = {
    ...leafCol,
    headerName: optionLabel,
    headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_1, ...zebraClassNames],
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
    mainMenuItems: undefined,
    headerComponent: EditablePlanOptionHeader,
    headerComponentParams: { planId: plan.id, optionLabel },
  };

  return {
    groupId: `${group.id}-option-${plan.id}`,
    headerName: tierHeaderName,
    headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_1, ...zebraClassNames],
    suppressHeaderContextMenu: !planLevelMenuItems,
    marryChildren: true,
    context: createColumnContext(group, plan, groupHeader),
    mainMenuItems: planLevelMenuItems,
    ...(planLevelMenuItems && {
      headerGroupComponent: PlanOptionGroupHeader,
    }),
    children: [optionLeaf],
  };
};

/**
 * Creates column definition for plan options only strategy (2-tier)
 *
 * @param group - Benefit plan group
 * @param zebraClassNames - Zebra stripe class names
 * @param leafBehaviorBuilder - Builder for leaf column behavior based on strategy
 * @param alternateIndex - Optional index for alternate plans to differentiate header display
 * @returns Column group definition
 */
const createPlanOptionsOnlyColumnsFromGroup = (
  group: BenefitPlanGroup,
  zebraClassNames: string[],
  leafBehaviorBuilder: LeafColumnBehaviorBuilder,
  planLevelMenuItems?: GetMainMenuItems,
  alternateIndex?: number,
  planDocumentMap?: PlanDocumentMap,
  // See createPlanOptionsAndNetworksColumnsFromGroup. Identifiers are also
  // shared across carriers in a side-by-side grid, so keying colId off them
  // there yields duplicate column ids.
  useFieldAsColId = false,
  // Needed by the BTX Plan Level dropdown (PlanLevelHeader) to persist option
  // name changes; only rendered when enablePlanLevelDropdown is set below.
  rfpId?: string,
  // Explicit seam: the BTX Plan Level dropdown is a Build-only concern, so it
  // must not key off planDocumentMap alone — see createPlanOptionsAndNetworksColumnsFromGroup.
  enablePlanLevelDropdown = false,
): ColGroupDef => {
  const groupHeader = formatPolicyPeriodDisplay(
    group.policyPeriod,
    group.nickname,
    alternateIndex,
    group.isHistorical,
  );

  return {
    headerName: groupHeader,
    marryChildren: true,
    groupId: `group-${group.id}`,
    context: createColumnContext(group),
    headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_0, ...zebraClassNames],
    children: group.benefitPlans.map((plan) => {
      const planHeader = formatOptionHeader(plan);

      const leafCol: ColDef = {
        colId: useFieldAsColId ? plan.id : plan.identifier,
        field: plan.id,
        headerName: planDocumentMap ? plan.identifier : planHeader,
        suppressHeaderContextMenu: !planLevelMenuItems,
        suppressHeaderMenuButton: !planLevelMenuItems,
        context: createColumnContext(group, plan, groupHeader),
        headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_1, ...zebraClassNames],
        mainMenuItems: planLevelMenuItems,
        cellClass: (params) => {
          const excelRowClass = isChildRow(params)
            ? ""
            : CLASSNAMES.EXCEL_PARENT_ROW;
          return [
            CLASSNAMES.BENEFIT_CELL,
            CLASSNAMES.EXCEL_CENTER_ALIGN,
            excelRowClass,
            ...zebraClassNames,
          ].filter(Boolean);
        },
        headerTooltip: plan.identifier,
        cellRendererParams: {
          hierarchy: buildHierarchy(groupHeader, planHeader),
          isNetworkBenefit: false,
        },
        flex: 1,
        minWidth: MIN_COLUMN_WIDTH, // Consistent width for aligned grid sync with RatesGrid/SummaryGrid
        ...leafBehaviorBuilder.build(),
      };

      if (planDocumentMap) {
        const showPlanLevelDropdown = enablePlanLevelDropdown;

        // Plan Group ID row — carries the 3-dot plan menu (moved down from
        // the Plan Level row above) only when the dropdown is enabled;
        // otherwise this tier stays a plain label, as it always has for MPX.
        leafCol.suppressHeaderContextMenu = showPlanLevelDropdown
          ? !planLevelMenuItems
          : true;
        leafCol.suppressHeaderMenuButton = showPlanLevelDropdown
          ? !planLevelMenuItems
          : true;
        leafCol.mainMenuItems = showPlanLevelDropdown
          ? planLevelMenuItems
          : undefined;
        leafCol.headerClass = [
          CLASSNAMES.HEADER_COLUMN_LEVEL_2,
          ...zebraClassNames,
        ];
        const optionGroup: ColGroupDef = {
          groupId: `${group.id}-option-${plan.id}`,
          headerName: formatEnumValue(plan.optionName) || plan.identifier,
          headerClass: [
            CLASSNAMES.HEADER_COLUMN_LEVEL_1,
            ...zebraClassNames,
            ...(showPlanLevelDropdown && !isValuePresent(plan.optionName)
              ? [CLASSNAMES.HEADER_REQUIRED_MISSING]
              : []),
          ],
          marryChildren: true,
          context: createColumnContext(group, plan, groupHeader),
          suppressHeaderContextMenu: showPlanLevelDropdown
            ? true
            : !planLevelMenuItems,
          mainMenuItems: showPlanLevelDropdown ? undefined : planLevelMenuItems,
          ...(showPlanLevelDropdown
            ? {
                headerGroupComponent: PlanLevelHeader,
                headerGroupComponentParams: {
                  rfpId,
                  benefitPlanId: plan.id,
                  optionName: plan.optionName,
                },
              }
            : planLevelMenuItems && {
                headerGroupComponent: PlanOptionGroupHeader,
              }),
          children: [leafCol],
        };
        return wrapWithDocumentHeader(
          optionGroup,
          plan.id,
          planDocumentMap,
          zebraClassNames,
        );
      }

      // Quotes SBS grid (no networks): give the value leaf the same editable
      // Plan Option header as Dental PPO (CD-1261). With no network sub-columns
      // the editable option is the leaf's own header, so it renders as a single
      // Plan Option row under the plan-name tier.
      if (useFieldAsColId) {
        return wrapWithEditablePlanOptionTier(
          group,
          plan,
          groupHeader,
          leafCol,
          planLevelMenuItems,
          zebraClassNames,
          planTierHeaderName(plan, group.isHistorical),
        );
      }

      return wrapWithDocumentHeader(
        leafCol,
        plan.id,
        planDocumentMap,
        zebraClassNames,
      );
    }),
  };
};

/**
 * Creates column definition for no grouping strategy (1-tier)
 *
 * @param group - Benefit plan group
 * @param zebraClassNames - Zebra stripe class names
 * @param leafBehaviorBuilder - Builder for leaf column behavior based on strategy
 * @param alternateIndex - Optional index for alternate plans to differentiate header display
 * @returns Column definition
 */
const createNoGroupColumnsFromGroup = (
  group: BenefitPlanGroup,
  zebraClassNames: string[],
  leafBehaviorBuilder: LeafColumnBehaviorBuilder,
  planLevelMenuItems?: GetMainMenuItems,
  alternateIndex?: number,
  planDocumentMap?: PlanDocumentMap,
  // The carrier-grouped (Quotes/MPX) grid keys colId off plan.id; it also owns
  // its plan menu at the leaf (that's where Copy/Paste Plan lives), so its leaf
  // must keep planLevelMenuItems rather than defer to a grid-level menu.
  useFieldAsColId = false,
  // True when a current-policy band will wrap this leaf, making it an inner
  // header. A lone leaf (no band) is the top header and must expose the plan
  // group menu, so it defers to the grid-level menu instead of the plan-only one.
  willWrapInBand = false,
  // Needed by the BTX Plan Level dropdown (PlanLevelHeader) to persist option
  // name changes; only rendered when enablePlanLevelDropdown is set below.
  rfpId?: string,
  // Explicit seam: the BTX Plan Level dropdown is a Build-only concern, so it
  // must not key off planDocumentMap alone — see createPlanOptionsAndNetworksColumnsFromGroup.
  enablePlanLevelDropdown = false,
): ColDef | ColGroupDef => {
  const firstPlan = group.benefitPlans[0];
  const groupHeader =
    formatPolicyPeriodDisplay(
      group.policyPeriod,
      group.nickname,
      alternateIndex,
      group.isHistorical,
    ) || "Plan";

  const leafCol: ColDef = {
    field: firstPlan.id,
    headerName: groupHeader,
    context: createColumnContext(group, firstPlan, groupHeader),
    headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_0, ...zebraClassNames],
    suppressHeaderContextMenu: !planLevelMenuItems,
    mainMenuItems: planLevelMenuItems,
    suppressHeaderMenuButton: !planLevelMenuItems,
    cellClass: (params) => {
      const excelRowClass = isChildRow(params)
        ? ""
        : CLASSNAMES.EXCEL_PARENT_ROW;
      return [
        CLASSNAMES.BENEFIT_CELL,
        CLASSNAMES.EXCEL_CENTER_ALIGN,
        excelRowClass,
        ...zebraClassNames,
      ].filter(Boolean);
    },
    headerTooltip: firstPlan.identifier,
    cellRendererParams: {
      hierarchy: buildHierarchy(groupHeader),
      isNetworkBenefit: false,
    },
    flex: 1,
    minWidth: MIN_COLUMN_WIDTH,
    ...leafBehaviorBuilder.build(),
  };

  if (!planDocumentMap) {
    // Quotes SBS grid (single-plan, no networks): give the value leaf the same
    // editable Plan Option header as Dental PPO (CD-1261) — one option row on
    // the leaf's own header under the plan-name tier.
    if (useFieldAsColId) {
      return wrapWithEditablePlanOptionTier(
        group,
        firstPlan,
        groupHeader,
        leafCol,
        planLevelMenuItems,
        zebraClassNames,
        planTierHeaderName(firstPlan, group.isHistorical),
      );
    }
    // Quotes' carrier-grouped leaves (useFieldAsColId) own their plan-level menu
    // — that's where Copy/Paste Plan lives — and a leaf wrapped by a current-
    // policy band is an inner header, so both keep planLevelMenuItems. A top-
    // level lone header in the PlanDesigner flow instead defers to the grid-level
    // getMainMenuItems, which offers the plan-group actions and folds in the plan
    // actions for this single-plan header.
    if (willWrapInBand) return leafCol;
    return {
      ...leafCol,
      suppressHeaderContextMenu: false,
      mainMenuItems: undefined,
    };
  }

  // With doc map: promote policyPeriod/nickname to outer group so the depth
  // matches plan_options_only (4-tier: group → doc → option name → plan ID leaf).
  // The leaf becomes the plan ID and the option name gets its own group above it.
  leafCol.headerName = firstPlan.identifier;
  leafCol.headerTooltip = firstPlan.identifier;
  leafCol.headerClass = [CLASSNAMES.HEADER_COLUMN_LEVEL_2, ...zebraClassNames];
  leafCol.context = createColumnContext(group, firstPlan, groupHeader);
  // Plan Group ID row — carries the 3-dot plan menu (moved down from the
  // Plan Level row below) only when the dropdown is enabled; otherwise this
  // tier stays a plain label, as it always has for MPX.
  leafCol.suppressHeaderContextMenu = enablePlanLevelDropdown
    ? !planLevelMenuItems
    : true;
  leafCol.suppressHeaderMenuButton = enablePlanLevelDropdown
    ? !planLevelMenuItems
    : true;
  leafCol.mainMenuItems = enablePlanLevelDropdown
    ? planLevelMenuItems
    : undefined;
  const optionGroup: ColGroupDef = {
    headerName: formatEnumValue(firstPlan.optionName) || firstPlan.identifier,
    headerClass: [
      CLASSNAMES.HEADER_COLUMN_LEVEL_1,
      ...zebraClassNames,
      ...(enablePlanLevelDropdown && !isValuePresent(firstPlan.optionName)
        ? [CLASSNAMES.HEADER_REQUIRED_MISSING]
        : []),
    ],
    marryChildren: true,
    context: createColumnContext(group, firstPlan, groupHeader),
    suppressHeaderContextMenu: enablePlanLevelDropdown
      ? true
      : !planLevelMenuItems,
    mainMenuItems: enablePlanLevelDropdown ? undefined : planLevelMenuItems,
    ...(enablePlanLevelDropdown
      ? {
          headerGroupComponent: PlanLevelHeader,
          headerGroupComponentParams: {
            rfpId,
            benefitPlanId: firstPlan.id,
            optionName: firstPlan.optionName,
          },
        }
      : planLevelMenuItems && { headerGroupComponent: PlanOptionGroupHeader }),
    children: [leafCol],
  };
  const wrappedCol = wrapWithDocumentHeader(
    optionGroup,
    firstPlan.id,
    planDocumentMap,
    zebraClassNames,
  );
  return {
    headerName: groupHeader,
    marryChildren: true,
    headerClass: [CLASSNAMES.HEADER_COLUMN_LEVEL_0, ...zebraClassNames],
    context: createColumnContext(group, firstPlan, groupHeader),
    children: [wrappedCol],
  } as ColGroupDef;
};

/**
 * Coverage identity needed to render the Current Carrier - Policy dropdown in
 * a current group's header (ADR 10.22). Present only when the feature is on.
 */
export interface CurrentPolicyHeaderContext {
  coverageId: string;
  rfpId: string;
  coverageType: CoverageTypeEnum;
}

/**
 * For a current plan group (with the feature on), swaps the column-group header
 * for the Current Carrier - Policy dropdown component (ADR 10.22). Non-current
 * groups and the flag-off path are returned unchanged.
 */
const applyCurrentPolicyHeader = (
  column: ColDef | ColGroupDef,
  group: BenefitPlanGroup,
  ctx?: CurrentPolicyHeaderContext,
): ColDef | ColGroupDef => {
  if (!ctx) return column;

  if (group.policyPeriod !== BaseBenefitPlanPolicyPeriod.current) {
    // FF is on but this is not a current group. Insert one empty header tier so
    // column depth stays aligned with the current group (which gains a dropdown
    // row between its outer group header and its plan children).
    if (!("children" in column)) {
      const leafCol = column as ColDef;
      return {
        headerName: leafCol.headerName ?? "",
        headerClass: leafCol.headerClass,
        headerTooltip: leafCol.headerTooltip,
        // Carry the plan context onto the promoted group so the top-header
        // menu can resolve the plan group (and, for a single-plan group, the
        // plan itself).
        context: leafCol.context as ColumnContext | undefined,
        marryChildren: true,
        children: [
          {
            headerName: "",
            marryChildren: true,
            children: [{ ...leafCol, headerName: "" }],
          },
        ],
      } as ColGroupDef;
    }
    const { children, ...outerProps } = column as ColGroupDef;
    return {
      ...outerProps,
      marryChildren: true,
      children: [{ headerName: "", marryChildren: true, children }],
    } as ColGroupDef;
  }

  const headerGroupComponentParams = {
    coverageId: ctx.coverageId,
    rfpId: ctx.rfpId,
    coverageType: ctx.coverageType,
    productType: group.productType,
    groupId: group.id,
    currentPolicyUuid: group.currentPolicyUuid,
  };

  // First header row: the original group label (same as FF-off).
  // Second header row: the In-Force Policy Name with the dropdown component.
  const dropdownRowBase = {
    headerName: CURRENT_POLICY_HEADER_NAME,
    headerGroupComponent: "CurrentPolicyGroupHeader",
    headerGroupComponentParams,
    marryChildren: true,
  };

  // Single-plan groups with no document map come back as a leaf ColDef.
  // Promote its headerName to the outer group and clear it from the leaf.
  if (!("children" in column)) {
    const leafCol = column as ColDef;
    return {
      headerName: leafCol.headerName ?? "",
      context: leafCol.context as ColumnContext | undefined,
      marryChildren: true,
      children: [
        { ...dropdownRowBase, children: [{ ...leafCol, headerName: "" }] },
      ],
    } as ColGroupDef;
  }

  // ColGroupDef: preserve all outer props (headerName, headerClass, context…),
  // inject the dropdown row between the outer group and its original children.
  const { children, ...outerProps } = column as ColGroupDef;
  return {
    ...outerProps,
    marryChildren: true,
    children: [{ ...dropdownRowBase, children }],
  } as ColGroupDef;
};

/**
 * Creates benefit plan columns from benefit plan groups based on grouping strategy.
 *
 * @param benefitPlanGroups - Array of benefit plan groups
 * @param networks - Available network options
 * @param leafBehaviorBuilder - Builder for leaf column behavior based on strategy
 * @returns Array of column definitions
 */
export const createBenefitPlanColumnsFromGroups = (
  benefitPlanGroups: BenefitPlanGroup[],
  networks: readonly string[],
  leafBehaviorBuilder: LeafColumnBehaviorBuilder,
  planDocumentMap?: PlanDocumentMap,
  planLevelMenuItems?: GetMainMenuItems,
  currentPolicyHeaderCtx?: CurrentPolicyHeaderContext,
  useFieldAsColId = false,
  // Force the plan-option tier on single-plan groups so their header depth
  // matches multi-plan groups elsewhere in the same grid (carrier-grouped
  // grids only — see buildCarrierGroupedColumns).
  forcePlanOptionTier = false,
  // Needed by the BTX Plan Level dropdown (PlanLevelHeader) to persist option
  // name changes; only rendered when enablePlanLevelDropdown is set below.
  rfpId?: string,
  // Explicit seam: true only for the BTX caller. MPX's carrier-grouped grid
  // (buildCarrierGroupedColumns) also passes a planDocumentMap, but for its
  // own "Source Document" header labels — it must not pick up the Plan Level
  // dropdown/menu swap by leaving this at its default (false).
  enablePlanLevelDropdown = false,
): (ColDef | ColGroupDef)[] => {
  const columns: (ColDef | ColGroupDef)[] = [];
  const hasNetworks = networks.length > 0;

  // Get all alternate plans for numbering
  const alternatePlans = benefitPlanGroups.filter(
    (group) => group.policyPeriod === BaseBenefitPlanPolicyPeriod.alternate,
  );

  // The Quotes grid drops the plan-group row: the carrier row above already
  // names the quote and each plan row below names its own column, so the tier
  // only repeated them. Its children are promoted in its place, keeping every
  // carrier the same depth whether it quoted one plan or several. The tier is
  // structural elsewhere, so only the carrier-grouped grid opts out — and only
  // when the carrier has a single plan group: a carrier column can legitimately
  // hold several independent groups (alternates, or the synthetic
  // Incumbent/Requested carrier on the Unmapped page), and each needs its own
  // header row to stay visually distinct from its siblings. Counted against
  // groups that actually render a column below (matching the empty-group skip
  // in the forEach) so a stray empty group can't force the tier to stay.
  const renderableGroupCount = benefitPlanGroups.filter(
    (group) => group.benefitPlans && group.benefitPlans.length > 0,
  ).length;
  const omitPlanGroupTier =
    useFieldAsColId && !planDocumentMap && renderableGroupCount === 1;

  benefitPlanGroups.forEach((group, index) => {
    // Handle empty groups
    if (!group.benefitPlans || group.benefitPlans.length === 0) {
      logger.warn(
        "buildColumnsFromBenefitPlanGroups: Empty benefit plan group",
        {
          groupId: group.id,
          nickname: group.nickname,
          position: group.position,
        },
      );
      return;
    }

    // Determine alternate index for numbered headers (e.g. "Alternate 1", "Alternate 1 - Custom Plan")
    const alternateIndex =
      group.policyPeriod === BaseBenefitPlanPolicyPeriod.alternate
        ? alternatePlans.findIndex((alt) => alt.id === group.id) + 1
        : undefined;

    // Calculate zebra striping based on iteration index (0-based)
    const zebraClassNames = [
      ...calculateZebraClassNames(index, group.policyPeriod),
      // Pending and historical part company below the header too: purple asks the analyst
      // for a decision, grey says the quote is out of play. `cell-bpg--pending-requote`
      // keeps its name because the outline CSS keys on it.
      ...(group.isPendingRequote
        ? ["header-bpg--pending-requote", "cell-bpg--pending-requote"]
        : []),
      ...(group.isHistorical && !group.isPendingRequote
        ? ["header-bpg--historical", "cell-bpg--historical"]
        : []),
    ];

    // Determine grouping strategy
    const hasMultiplePlans = group.benefitPlans.length > 1;

    const pushGroupColumn = (column: ColDef | ColGroupDef): void => {
      const banded = applyCurrentPolicyHeader(
        column,
        group,
        currentPolicyHeaderCtx,
      );
      if (omitPlanGroupTier && "children" in banded) {
        columns.push(...(banded.children as (ColDef | ColGroupDef)[]));
        return;
      }
      columns.push(banded);
    };

    if (hasNetworks) {
      // Strategy: plan_options_and_networks (3-tier)
      // Always use this when networks exist, even for single-plan BPGs,
      // so the option name header (e.g. "Not Applicable") is shown
      // consistently and header depths stay aligned.
      pushGroupColumn(
        createPlanOptionsAndNetworksColumnsFromGroup(
          group,
          networks,
          zebraClassNames,
          leafBehaviorBuilder,
          planLevelMenuItems,
          alternateIndex,
          planDocumentMap,
          useFieldAsColId,
          rfpId,
          enablePlanLevelDropdown,
        ),
      );
    } else if (hasMultiplePlans || forcePlanOptionTier) {
      // Strategy: plan_options_only (2-tier)
      pushGroupColumn(
        createPlanOptionsOnlyColumnsFromGroup(
          group,
          zebraClassNames,
          leafBehaviorBuilder,
          planLevelMenuItems,
          alternateIndex,
          planDocumentMap,
          useFieldAsColId,
          rfpId,
          enablePlanLevelDropdown,
        ),
      );
    } else {
      // Strategy: no_group (1-tier)
      columns.push(
        applyCurrentPolicyHeader(
          createNoGroupColumnsFromGroup(
            group,
            zebraClassNames,
            leafBehaviorBuilder,
            planLevelMenuItems,
            alternateIndex,
            planDocumentMap,
            useFieldAsColId,
            !!currentPolicyHeaderCtx,
            rfpId,
            enablePlanLevelDropdown,
          ),
          group,
          currentPolicyHeaderCtx,
        ),
      );
    }
  });

  return columns;
};

export const buildColumnsFromBenefitPlanGroups = (
  benefitPlanGroups: BenefitPlanGroup[],
  coverage: RfpCoverage,
  disableNetworkColumns = false,
  strategy: ColumnBuildStrategy,
  planDocumentMap?: PlanDocumentMap,
  planLevelMenuItems?: GetMainMenuItems,
  currentCarrierPolicyDropdownEnabled = false,
  // Explicit seam: the BTX Plan Level dropdown, set by the BTX caller only —
  // never inferred from planDocumentMap, which MPX's carrier-grouped grid
  // also sets for its own unrelated document-label header tiers.
  enablePlanLevelDropdown = false,
): (ColDef | ColGroupDef)[] => {
  // Validate inputs
  if (!benefitPlanGroups || benefitPlanGroups.length === 0) {
    logger.warn(
      "buildColumnsFromBenefitPlanGroups: No benefit plan groups provided",
      {
        coverageType: coverage?.type,
      },
    );
    return [];
  }

  if (!coverage?.benefitPlans || !Array.isArray(coverage.benefitPlans)) {
    logger.warn(
      "buildColumnsFromBenefitPlanGroups: No coverage benefit plans",
      {
        coverageType: coverage?.type,
      },
    );
    return [];
  }

  // Initialize column definitions array
  const columnDefs: (ColDef | ColGroupDef)[] = [];

  // Get available networks for this coverage type
  const networks = disableNetworkColumns
    ? []
    : getNetworks(benefitPlanGroups, coverage);

  const hasCurrentGroup = benefitPlanGroups.some(
    (group) => group.policyPeriod === BaseBenefitPlanPolicyPeriod.current,
  );

  // Current Carrier - Policy header dropdown context (ADR 10.22), only when
  // the feature is on and a current group exists to carry the dropdown row.
  // Without a current group (e.g. a new coverage) the non-current groups'
  // alignment tier would have nothing to align with, while the benefits column
  // stays unpadded, pushing every plan header one row below its label.
  const currentPolicyHeaderCtx: CurrentPolicyHeaderContext | undefined =
    currentCarrierPolicyDropdownEnabled && hasCurrentGroup
      ? {
          coverageId: coverage.id,
          rfpId: coverage.rfpId,
          coverageType: coverage.coverageType,
        }
      : undefined;

  // Build columns from benefit plan groups
  const benefitPlanColumns = createBenefitPlanColumnsFromGroups(
    benefitPlanGroups,
    networks,
    strategy.getLeafColumnBehaviorBuilder(),
    planDocumentMap,
    planLevelMenuItems,
    currentPolicyHeaderCtx,
    undefined,
    undefined,
    coverage.rfpId,
    enablePlanLevelDropdown,
  );

  // Add static column for benefits (with labelled tiers when doc map present).
  // When the feature is on and the coverage has a current group, label the top
  // header tier "Current Policy" to head the group-header dropdown row.
  const hasNetworks = networks.length > 0;
  const currentPolicyLabel = currentPolicyHeaderCtx
    ? CURRENT_POLICY_HEADER_NAME
    : undefined;
  columnDefs.push(
    createLabelledBenefitsColumn(
      planDocumentMap,
      hasNetworks,
      currentPolicyLabel,
      // Matches the "Plan Level" header naming the BTX dropdown renders on
      // the plan columns themselves — only when that seam is enabled.
      enablePlanLevelDropdown ? PLAN_LEVEL_TIER_LABEL : undefined,
    ),
  );

  columnDefs.push(...benefitPlanColumns);

  return columnDefs;
};

/**
 * Determines which column building strategy to use and builds columns accordingly
 *
 * @param benefitPlanGroups - Optional benefit plan groups from API
 * @param coverage - Coverage data
 * @param disableNetworkColumns - Whether to disable network sub-columns based on business rules
 * @param strategy - Column building strategy to determine grouping and column behavior
 * @returns Column definitions for AG Grid
 */
export const buildColumns = (
  benefitPlanGroups: BenefitPlanGroup[] | undefined,
  coverage: RfpCoverage,
  disableNetworkColumns = false,
  strategy: ColumnBuildStrategy,
  planDocumentMap?: PlanDocumentMap,
  planLevelMenuItems?: GetMainMenuItems,
  currentCarrierPolicyDropdownEnabled = false,
  // Explicit seam: the BTX Plan Level dropdown, set by the BTX caller only —
  // never inferred from planDocumentMap, which MPX's carrier-grouped grid
  // also sets for its own unrelated document-label header tiers.
  enablePlanLevelDropdown = false,
): (ColDef | ColGroupDef)[] => {
  // Use API-driven column building if benefit plan groups are available
  if (benefitPlanGroups && benefitPlanGroups.length > 0) {
    return buildColumnsFromBenefitPlanGroups(
      benefitPlanGroups,
      coverage,
      disableNetworkColumns,
      strategy,
      planDocumentMap,
      planLevelMenuItems,
      currentCarrierPolicyDropdownEnabled,
      enablePlanLevelDropdown,
    );
  }

  return [];
};
